import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, Button, Grid, Chip, LinearProgress, Divider,
  AppBar, Toolbar, IconButton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, CircularProgress, Tabs, Tab, Avatar, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import {
  ArrowBack, Group, Add, Refresh, AccountBalance, TrendingUp, Remove,
  CheckCircle, Warning, Error, Star, VerifiedUser, Timeline, SwapHoriz
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import strictMonochromeTheme from '../utils/strictMonochromeTheme';
import { ethers } from 'ethers';
import Notification from './Notification';

const Delegators = () => {
  const navigate = useNavigate();
  const [delegations, setDelegations] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [validators, setValidators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [delegateDialog, setDelegateDialog] = useState(false);
  const [undelegateDialog, setUndelegateDialog] = useState(false);
  const [redelegateDialog, setRedelegateDialog] = useState(false);
  const [selectedValidator, setSelectedValidator] = useState(null);
  const [targetValidator, setTargetValidator] = useState(null);
  const [actionAmount, setActionAmount] = useState('');
  const [isAddingStake, setIsAddingStake] = useState(false);
  const [networkStats, setNetworkStats] = useState({
    total_delegations: 0,
    total_delegated_avo: 0,
    total_rewards_avo: 0,
    avg_apr: 0
  });
  
  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info',
    details: null
  });

  // Función para obtener delegaciones y recompensas
  const fetchDelegationData = useCallback(async () => {
    console.log('🔍 [DELEGATORS] Fetching delegation data from AVO RPC...');
    setLoading(true);
    
    try {
      const rpcUrl = 'http://localhost:9545';
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      
      // Obtener todas las delegaciones
      console.log('📡 [DELEGATORS] Calling avo_getAllDelegations...');
      const delegationsData = await provider.send('avo_getAllDelegations', []);
      console.log('✅ [DELEGATORS] Delegations response:', delegationsData);
      
      // Obtener todos los validadores
      console.log('📡 [DELEGATORS] Calling avo_getValidators...');
      const validatorsData = await provider.send('avo_getValidators', []);
      console.log('✅ [DELEGATORS] Validators response:', validatorsData);
      
      if (!Array.isArray(delegationsData) || !Array.isArray(validatorsData)) {
        console.error('❌ [DELEGATORS] Invalid response format');
        setDelegations([]);
        setValidators([]);
        setLoading(false);
        return;
      }

      // Obtener dirección del usuario actual
      const userAddress = localStorage.getItem('address')?.toLowerCase() || '';
      console.log('👤 [DELEGATORS] User address:', userAddress);

      // Filtrar delegaciones del usuario actual
      const userDelegations = delegationsData.filter(
        d => d.delegator_address?.toLowerCase() === userAddress
      );

      // Crear un mapa de validadores por ID
      const validatorMap = {};
      validatorsData.forEach(v => {
        validatorMap[v.id] = v;
      });

      // Mapear delegaciones con información del validador
      const mappedDelegations = userDelegations.map(delegation => {
        const validator = validatorMap[delegation.validator_id] || {};
        return {
          positionId: delegation.position_id,
          validatorId: delegation.validator_id,
          validatorAddress: validator.address || `validator_${delegation.validator_id}`,
          validatorName: `Validator #${delegation.validator_id}`,
          delegatedAmount: delegation.amount || 0,
          shares: delegation.amount ? delegation.amount.toFixed(18) : '0',
          commission: 5, // Default commission
          status: delegation.status === 'Active' ? 'bonded' : 'unbonding',
          reward: delegation.pending_rewards || 0,
          apr: (delegation.apr * 100) || 8,
          stakedAt: delegation.staked_at || 0
        };
      });

      console.log(`📋 [DELEGATORS] Processed ${mappedDelegations.length} user delegations`);

      // Filtrar validadores disponibles (no delegados por el usuario)
      const delegatedValidatorIds = new Set(userDelegations.map(d => d.validator_id));
      const availableValidators = validatorsData
        .filter(v => !delegatedValidatorIds.has(v.id) && v.status === 'Active')
        .map((validator, index) => ({
          id: validator.id,
          operatorAddress: validator.address || `avovaloper${validator.id}...`,
          moniker: `Validator #${validator.id}`,
          commission: 5, // Default commission
          votingPower: Math.round(validator.total_stake || 0),
          status: 'active',
          isDelegated: false,
          apr: (validator.apr * 100).toFixed(1)
        }));

      setDelegations(mappedDelegations);
      setValidators(availableValidators);

      // Calcular estadísticas
      const stats = {
        total_delegations: mappedDelegations.length,
        total_delegated_avo: mappedDelegations.reduce((sum, d) => sum + d.delegatedAmount, 0),
        total_rewards_avo: mappedDelegations.reduce((sum, d) => sum + d.reward, 0),
        avg_apr: mappedDelegations.length > 0
          ? (mappedDelegations.reduce((sum, d) => sum + d.apr, 0) / mappedDelegations.length).toFixed(1)
          : '0.0'
      };

      setNetworkStats(stats);
      console.log('📊 [DELEGATORS] Network stats:', stats);
      
    } catch (error) {
      console.error('❌ [DELEGATORS] Error fetching delegation data:', error);
      setDelegations([]);
      setValidators([]);
    }
    
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchDelegationData();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchDelegationData, 10000);
    return () => clearInterval(interval);
  }, [fetchDelegationData]);

  // Helper function for notifications
  const showNotification = (message, severity = 'info', details = null) => {
    setNotification({ open: true, message, severity, details });
  };

  const getTotalDelegated = () => {
    return delegations.reduce((total, delegation) => total + delegation.delegatedAmount, 0);
  };

  const getTotalRewards = () => {
    return delegations.reduce((total, delegation) => total + delegation.reward, 0);
  };

  const handleDelegate = async () => {
    console.log('🔄 [DELEGATORS] Starting delegation process...');
    
    try {
      const userAddress = localStorage.getItem('address');
      if (!userAddress) {
        showNotification('User address not found. Please log in again.', 'error');
        return;
      }

      const amount = parseFloat(actionAmount);
      if (isNaN(amount) || amount <= 0) {
        showNotification('Please enter a valid amount (can use decimals like 0.5)', 'warning');
        return;
      }

      const validatorId = selectedValidator?.id || selectedValidator?.validatorId;
      if (!validatorId) {
        showNotification('Please select a validator', 'warning');
        return;
      }

      const rpcUrl = 'http://localhost:9545';
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      
      console.log(`📡 [DELEGATORS] Delegating ${amount} AVO to validator ${validatorId}...`);
      console.log(`👤 [DELEGATORS] From address: ${userAddress}`);
      
      // RPC espera: [validator_id, delegator_address, amount]
      const result = await provider.send('avo_delegateToValidator', [
        validatorId,
        userAddress,
        amount
      ]);
      
      console.log('✅ [DELEGATORS] Delegation successful:', result);
      
      // Show success notification with details
      showNotification(
        `Successfully delegated ${amount} AVO to ${selectedValidator?.moniker || selectedValidator?.validatorName}!`,
        'success',
        {
          'Transaction Hash': result.transaction_hash?.slice(0, 20) + '...',
          'Gas Used': `${result.gas_used} units`,
          'Gas Fee': `${result.gas_fee_avo} AVO`,
          'Total Delegated': `${result.delegated_amount_avo} AVO`,
          'Signature': result.signature_verified ? '✓ Verified' : '✗ Not verified'
        }
      );
      
      setDelegateDialog(false);
      setSelectedValidator(null);
      setActionAmount('');
      
      // Refresh data
      fetchDelegationData();
      
    } catch (error) {
      console.error('❌ [DELEGATORS] Delegation error:', error);
      showNotification(`Error delegating: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const handleUndelegate = async () => {
    console.log('🔄 [DELEGATORS] Starting undelegation process...');
    
    try {
      const userAddress = localStorage.getItem('address');
      if (!userAddress) {
        showNotification('User address not found. Please log in again.', 'error');
        return;
      }

      const amount = parseFloat(actionAmount);
      if (isNaN(amount) || amount <= 0) {
        showNotification('Please enter a valid amount (can use decimals like 0.5)', 'warning');
        return;
      }

      if (amount > selectedValidator.delegatedAmount) {
        showNotification(
          `Cannot undelegate more than your delegated amount`,
          'error',
          { 'Delegated Amount': `${selectedValidator.delegatedAmount.toFixed(2)} AVO` }
        );
        return;
      }

      const rpcUrl = 'http://localhost:9545';
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      
      console.log(`📡 [DELEGATORS] Undelegating ${amount} AVO from validator ${selectedValidator.validatorId}...`);
      console.log(`👤 [DELEGATORS] From address: ${userAddress}`);
      
      // RPC espera: [validator_id, delegator_address, amount]
      const result = await provider.send('avo_undelegateFromValidator', [
        selectedValidator.validatorId,
        userAddress,
        amount
      ]);
      
      console.log('✅ [DELEGATORS] Undelegation successful:', result);
      
      showNotification(
        `Successfully undelegated ${amount} AVO from ${selectedValidator.validatorName}!`,
        'success',
        {
          'Transaction Hash': result.transaction_hash?.slice(0, 20) + '...',
          'Rewards Claimed': `${result.rewards_claimed} AVO`,
          'Gas Fee': `${result.gas_fee} AVO`
        }
      );
      
      setUndelegateDialog(false);
      setSelectedValidator(null);
      setActionAmount('');
      
      // Refresh data
      fetchDelegationData();
      
    } catch (error) {
      console.error('❌ [DELEGATORS] Undelegation error:', error);
      showNotification(`Error undelegating: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const handleRedelegate = async () => {
    console.log('🔄 [DELEGATORS] Starting redelegation process...');
    
    try {
      const userAddress = localStorage.getItem('address');
      if (!userAddress) {
        showNotification('User address not found. Please log in again.', 'error');
        return;
      }

      if (!targetValidator) {
        showNotification('Please select a target validator', 'warning');
        return;
      }

      const amount = parseFloat(actionAmount);
      if (isNaN(amount) || amount <= 0) {
        showNotification('Please enter a valid amount (can use decimals like 0.5)', 'warning');
        return;
      }

      if (amount > selectedValidator.delegatedAmount) {
        showNotification(
          'Cannot redelegate more than your delegated amount',
          'error',
          { 'Delegated Amount': `${selectedValidator.delegatedAmount.toFixed(2)} AVO` }
        );
        return;
      }

      if (selectedValidator.validatorId === targetValidator.id || selectedValidator.validatorId === targetValidator.validatorId) {
        showNotification('Cannot redelegate to the same validator', 'warning');
        return;
      }

      const rpcUrl = 'http://localhost:9545';
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      
      console.log(`📡 [DELEGATORS] Redelegating ${amount} AVO from validator ${selectedValidator.validatorId} to ${targetValidator.id || targetValidator.validatorId}...`);
      
      // RPC espera: [from_validator_id, to_validator_id, delegator_address, amount]
      const result = await provider.send('avo_redelegateToValidator', [
        selectedValidator.validatorId,
        targetValidator.id || targetValidator.validatorId,
        userAddress,
        amount
      ]);
      
      console.log('✅ [DELEGATORS] Redelegation successful:', result);
      
      showNotification(
        `Successfully redelegated ${amount} AVO!`,
        'success',
        {
          'From': selectedValidator.validatorName,
          'To': targetValidator.moniker || targetValidator.validatorName,
          'Transaction Hash': result.transaction_hash?.slice(0, 20) + '...',
          'Gas Fee': `${result.gas_fee_avo} AVO`
        }
      );
      
      setRedelegateDialog(false);
      setSelectedValidator(null);
      setTargetValidator(null);
      setActionAmount('');
      
      // Refresh data
      fetchDelegationData();
      
    } catch (error) {
      console.error('❌ [DELEGATORS] Redelegation error:', error);
      showNotification(`Error redelegating: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const handleUndelegateAll = async () => {
    console.log('🔄 [DELEGATORS] Starting full undelegation...');
    
    try {
      const userAddress = localStorage.getItem('address');
      if (!userAddress) {
        showNotification('User address not found. Please log in again.', 'error');
        return;
      }

      if (!selectedValidator) {
        showNotification('No validator selected', 'warning');
        return;
      }

      const confirmMsg = `Are you sure you want to undelegate ALL ${selectedValidator.delegatedAmount.toFixed(2)} AVO + claim all rewards (${selectedValidator.reward.toFixed(6)} AVO) from ${selectedValidator.validatorName}?\n\nThis action cannot be undone.`;
      if (!window.confirm(confirmMsg)) {
        return;
      }

      const rpcUrl = 'http://localhost:9545';
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      
      console.log(`📡 [DELEGATORS] Full undelegation from validator ${selectedValidator.validatorId}...`);
      
      // RPC espera: [validator_id, delegator_address]
      const result = await provider.send('avo_undelegateAll', [
        selectedValidator.validatorId,
        userAddress
      ]);
      
      console.log('✅ [DELEGATORS] Full undelegation successful:', result);
      
      const totalReturned = parseFloat(result.undelegated_amount) + parseFloat(result.rewards_claimed);
      
      showNotification(
        `Successfully withdrew everything from ${selectedValidator.validatorName}!`,
        'success',
        {
          'Undelegated': `${result.undelegated_amount} AVO`,
          'Rewards Claimed': `${result.rewards_claimed} AVO`,
          'Total Returned': `${totalReturned.toFixed(6)} AVO`,
          'Transaction Hash': result.transaction_hash?.slice(0, 20) + '...',
          'Gas Fee': `${result.gas_fee} AVO`
        }
      );
      
      setUndelegateDialog(false);
      setSelectedValidator(null);
      
      // Refresh data
      fetchDelegationData();
      
    } catch (error) {
      console.error('❌ [DELEGATORS] Full undelegation error:', error);
      showNotification(`Error undelegating: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const handleAddStake = async () => {
    console.log('🔄 [DELEGATORS] Adding more stake...');
    
    try {
      const userAddress = localStorage.getItem('address');
      if (!userAddress) {
        showNotification('User address not found. Please log in again.', 'error');
        return;
      }

      const amount = parseFloat(actionAmount);
      if (isNaN(amount) || amount <= 0) {
        showNotification('Please enter a valid amount (can use decimals like 0.5)', 'warning');
        return;
      }

      const rpcUrl = 'http://localhost:9545';
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      
      console.log(`📡 [DELEGATORS] Adding ${amount} AVO to validator ${selectedValidator.validatorId}...`);
      
      // RPC espera: [validator_id, delegator_address, amount]
      const result = await provider.send('avo_addToDelegation', [
        selectedValidator.validatorId,
        userAddress,
        amount
      ]);
      
      console.log('✅ [DELEGATORS] Stake added successfully:', result);
      
      showNotification(
        `Successfully added ${amount} AVO to ${selectedValidator.validatorName}!`,
        'success',
        {
          'Added Amount': `${result.added_amount_avo} AVO`,
          'New Total Stake': `${result.total_stake_avo} AVO`,
          'Transaction Hash': result.transaction_hash?.slice(0, 20) + '...',
          'Gas Fee': `${result.gas_fee_avo} AVO`
        }
      );
      
      setDelegateDialog(false);
      setSelectedValidator(null);
      setActionAmount('');
      
      // Refresh data
      fetchDelegationData();
      
    } catch (error) {
      console.error('❌ [DELEGATORS] Add stake error:', error);
      showNotification(`Error adding stake: ${error.message || 'Unknown error'}`, 'error');
    }
  };

  const handleClaimRewards = (validator = null) => {
    // Las recompensas se reclaman automáticamente al hacer undelegate
    showNotification(
      'Rewards are automatically claimed when you undelegate. Use "UNDELEGATE" to claim partial rewards or "UNDELEGATE ALL" to withdraw everything including all rewards.',
      'info'
    );
  };

  const DelegationCard = ({ delegation }) => (
    <Card sx={{ 
      mb: 2, 
      backgroundColor: '#000000', 
      border: '1px solid #FFFFFF',
      borderRadius: 0
    }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ 
              bgcolor: '#000000', 
              border: '1px solid #FFFFFF',
              color: '#FFFFFF',
              width: 40, 
              height: 40 
            }}>
              {delegation.validatorName.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                {delegation.validatorName}
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                {delegation.validatorAddress.slice(0, 20)}...
              </Typography>
            </Box>
          </Box>
          <Chip 
            label={delegation.status.toUpperCase()} 
            sx={{ 
              backgroundColor: '#000000',
              border: '1px solid #FFFFFF',
              color: '#FFFFFF',
              fontWeight: 600,
              borderRadius: 0
            }}
          />
        </Stack>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={3}>
            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>DELEGATED</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              {delegation.delegatedAmount.toLocaleString()} AVO
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>COMMISSION</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              {delegation.commission}%
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>PENDING REWARDS</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              {delegation.reward.toFixed(2)} AVO
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>APR</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              {delegation.apr.toFixed(1)}%
            </Typography>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<Add sx={{ color: '#FFFFFF' }} />}
            size="small"
            onClick={() => {
              setSelectedValidator(delegation);
              setIsAddingStake(true);
              setDelegateDialog(true);
            }}
            sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0 }}
          >
            ADD STAKE
          </Button>
          <Button
            variant="outlined"
            startIcon={<Remove sx={{ color: '#FFFFFF' }} />}
            size="small"
            onClick={() => {
              setSelectedValidator(delegation);
              setUndelegateDialog(true);
            }}
            sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0 }}
          >
            UNDELEGATE
          </Button>
          <Button
            variant="outlined"
            startIcon={<SwapHoriz sx={{ color: '#FFFFFF' }} />}
            size="small"
            onClick={() => {
              setSelectedValidator(delegation);
              setRedelegateDialog(true);
            }}
            sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0 }}
          >
            REDELEGATE
          </Button>
          <Button
            variant="outlined"
            startIcon={<AccountBalance sx={{ color: '#FFFFFF' }} />}
            size="small"
            onClick={() => handleClaimRewards(delegation)}
            sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', borderRadius: 0 }}
          >
            CLAIM
          </Button>
        </Stack>
      </Box>
    </Card>
  );

  const ValidatorCard = ({ validator }) => (
    <Card sx={{ 
      mb: 2, 
      backgroundColor: '#000000', 
      border: '1px solid #FFFFFF',
      borderRadius: 0
    }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ 
              bgcolor: '#000000', 
              border: '1px solid #FFFFFF',
              color: '#FFFFFF',
              width: 40, 
              height: 40 
            }}>
              {validator.moniker.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                {validator.moniker}
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                {validator.operatorAddress.slice(0, 20)}...
              </Typography>
            </Box>
          </Box>
          <Chip 
            label="AVAILABLE" 
            sx={{ 
              backgroundColor: '#000000',
              border: '1px solid #FFFFFF',
              color: '#FFFFFF',
              fontWeight: 600,
              borderRadius: 0
            }}
          />
        </Stack>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>VOTING POWER</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              {(validator.votingPower / 1000000).toFixed(2)}M AVO
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>COMMISSION</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              {validator.commission}%
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>APR</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              {validator.apr}%
            </Typography>
          </Grid>
        </Grid>

        <Button
          variant="outlined"
          startIcon={<Add sx={{ color: '#FFFFFF' }} />}
          fullWidth
          onClick={() => {
            setSelectedValidator(validator);
            setIsAddingStake(false);
            setDelegateDialog(true);
          }}
          sx={{ 
            borderColor: '#FFFFFF', 
            color: '#FFFFFF',
            borderRadius: 0
          }}
        >
          DELEGATE
        </Button>
      </Box>
    </Card>
  );

  return (
    <ThemeProvider theme={strictMonochromeTheme}>
      <Box sx={{ minHeight: '100vh', backgroundColor: '#000000', color: '#FFFFFF' }}>
        {/* Header */}
        <AppBar position="static" sx={{ backgroundColor: '#000000', borderBottom: '1px solid #FFFFFF' }}>
          <Toolbar>
            <IconButton onClick={() => navigate('/dashboard')} sx={{ color: '#FFFFFF', mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Group sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, letterSpacing: '1px' }}>
              DELEGATORS
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AccountBalance />}
              onClick={() => handleClaimRewards()}
              sx={{ borderColor: '#FFFFFF', color: '#FFFFFF', mr: 2 }}
            >
              CLAIM ALL REWARDS
            </Button>
            <IconButton onClick={fetchDelegationData} sx={{ color: '#FFFFFF' }}>
              <Refresh />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 3 }}>
          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ 
                p: 2, 
                textAlign: 'center', 
                backgroundColor: '#000000', 
                border: '1px solid #FFFFFF',
                borderRadius: 0
              }}>
                <AccountBalance sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  {getTotalDelegated().toLocaleString()} AVO
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                  TOTAL DELEGATED
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ 
                p: 2, 
                textAlign: 'center', 
                backgroundColor: '#000000', 
                border: '1px solid #FFFFFF',
                borderRadius: 0
              }}>
                <TrendingUp sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  {getTotalRewards().toFixed(2)} AVO
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                  PENDING REWARDS
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ 
                p: 2, 
                textAlign: 'center', 
                backgroundColor: '#000000', 
                border: '1px solid #FFFFFF',
                borderRadius: 0
              }}>
                <VerifiedUser sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  {delegations.length}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                  VALIDATORS
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{ 
                p: 2, 
                textAlign: 'center', 
                backgroundColor: '#000000', 
                border: '1px solid #FFFFFF',
                borderRadius: 0
              }}>
                <Timeline sx={{ fontSize: 40, color: '#FFFFFF', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  {networkStats.avg_apr}%
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                  AVG APR
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs */}
          <Card sx={{ 
            mb: 3, 
            backgroundColor: '#000000', 
            border: '1px solid #FFFFFF',
            borderRadius: 0
          }}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              sx={{ 
                borderBottom: '1px solid #FFFFFF',
                '& .MuiTab-root': {
                  color: '#FFFFFF',
                  borderRadius: 0
                },
                '& .Mui-selected': {
                  color: '#FFFFFF !important'
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#FFFFFF'
                }
              }}
            >
              <Tab label="MY DELEGATIONS" />
              <Tab label="AVAILABLE VALIDATORS" />
            </Tabs>
          </Card>

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#FFFFFF' }} />
              <Typography sx={{ mt: 2, color: '#FFFFFF' }}>Loading delegation data...</Typography>
            </Box>
          ) : (
            <Box>
              {tabValue === 0 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#FFFFFF' }}>
                    MY DELEGATIONS ({delegations.length})
                  </Typography>
                  {delegations.length > 0 ? (
                    delegations.map((delegation, index) => (
                      <DelegationCard key={index} delegation={delegation} />
                    ))
                  ) : (
                    <Card sx={{ 
                      p: 4, 
                      textAlign: 'center', 
                      backgroundColor: '#000000', 
                      border: '1px solid #FFFFFF',
                      borderRadius: 0
                    }}>
                      <Group sx={{ fontSize: 60, color: '#FFFFFF', mb: 2 }} />
                      <Typography variant="h6" sx={{ mb: 1, color: '#FFFFFF' }}>
                        NO DELEGATIONS FOUND
                      </Typography>
                      <Typography sx={{ mb: 3, color: '#FFFFFF' }}>
                        You haven't delegated to any validators yet. Start earning rewards by delegating your AVO tokens.
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Add sx={{ color: '#FFFFFF' }} />}
                        onClick={() => setTabValue(1)}
                        sx={{ 
                          borderColor: '#FFFFFF', 
                          color: '#FFFFFF',
                          borderRadius: 0
                        }}
                      >
                        FIND VALIDATORS
                      </Button>
                    </Card>
                  )}
                </Box>
              )}

              {tabValue === 1 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#FFFFFF' }}>
                    AVAILABLE VALIDATORS ({validators.length})
                  </Typography>
                  {validators.length > 0 ? (
                    validators.map((validator) => (
                      <ValidatorCard key={validator.operatorAddress} validator={validator} />
                    ))
                  ) : (
                    <Card sx={{ 
                      p: 4, 
                      textAlign: 'center', 
                      backgroundColor: '#000000', 
                      border: '1px solid #FFFFFF',
                      borderRadius: 0
                    }}>
                      <VerifiedUser sx={{ fontSize: 60, color: '#FFFFFF', mb: 2 }} />
                      <Typography variant="h6" sx={{ mb: 1, color: '#FFFFFF' }}>
                        NO VALIDATORS AVAILABLE
                      </Typography>
                      <Typography sx={{ mb: 3, color: '#FFFFFF' }}>
                        All available validators have been delegated to.
                      </Typography>
                    </Card>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Delegate Dialog */}
        <Dialog 
          open={delegateDialog} 
          onClose={() => setDelegateDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#000000',
              border: '1px solid #FFFFFF',
              borderRadius: 0
            }
          }}
        >
          <DialogTitle sx={{ borderBottom: '1px solid #FFFFFF', color: '#FFFFFF' }}>
            {isAddingStake ? 'ADD STAKE TO DELEGATION' : 'DELEGATE TO VALIDATOR'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {selectedValidator && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: '#FFFFFF' }}>
                  {selectedValidator.moniker || selectedValidator.validatorName}
                </Typography>
                {isAddingStake && (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mb: 2, 
                      backgroundColor: '#000000', 
                      border: '1px solid #FFFFFF',
                      borderRadius: 0,
                      color: '#FFFFFF',
                      '& .MuiAlert-icon': {
                        color: '#FFFFFF'
                      }
                    }}
                  >
                    Current delegation: {selectedValidator.delegatedAmount?.toFixed(2) || '0.00'} AVO
                  </Alert>
                )}
                <TextField
                  fullWidth
                  label={isAddingStake ? "Amount to Add (AVO)" : "Amount to Delegate (AVO)"}
                  value={actionAmount}
                  onChange={(e) => setActionAmount(e.target.value)}
                  variant="outlined"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      borderRadius: 0,
                      '& fieldset': { borderColor: '#FFFFFF' },
                      '&:hover fieldset': { borderColor: '#FFFFFF' },
                      '&.Mui-focused fieldset': { borderColor: '#FFFFFF' }
                    },
                    '& .MuiInputLabel-root': { 
                      color: '#FFFFFF',
                      '&.Mui-focused': { color: '#FFFFFF' }
                    }
                  }}
                />
                <Alert 
                  severity="info" 
                  sx={{ 
                    mt: 2, 
                    backgroundColor: '#000000', 
                    border: '1px solid #FFFFFF',
                    borderRadius: 0,
                    color: '#FFFFFF',
                    '& .MuiAlert-icon': {
                      color: '#FFFFFF'
                    }
                  }}
                >
                  {isAddingStake 
                    ? 'Adding stake will increase your delegation and rewards immediately.'
                    : 'Delegating AVO tokens will start earning rewards immediately. You can undelegate at any time.'}
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid #FFFFFF' }}>
            <Button 
              onClick={() => {
                setDelegateDialog(false);
                setIsAddingStake(false);
              }}
              sx={{ color: '#FFFFFF', borderRadius: 0 }}
            >
              CANCEL
            </Button>
            <Button 
              onClick={isAddingStake ? handleAddStake : handleDelegate}
              variant="outlined"
              sx={{ 
                borderColor: '#FFFFFF', 
                color: '#FFFFFF',
                borderRadius: 0
              }}
            >
              {isAddingStake ? 'ADD STAKE' : 'DELEGATE'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Undelegate Dialog */}
        <Dialog 
          open={undelegateDialog} 
          onClose={() => setUndelegateDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#000000',
              border: '1px solid #FFFFFF',
              borderRadius: 0
            }
          }}
        >
          <DialogTitle sx={{ borderBottom: '1px solid #FFFFFF', color: '#FFFFFF' }}>
            UNDELEGATE FROM VALIDATOR
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {selectedValidator && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: '#FFFFFF' }}>
                  {selectedValidator.validatorName}
                </Typography>
                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 2, 
                    backgroundColor: '#000000', 
                    border: '1px solid #FFFFFF',
                    borderRadius: 0,
                    color: '#FFFFFF',
                    '& .MuiAlert-icon': {
                      color: '#FFFFFF'
                    }
                  }}
                >
                  Available: {selectedValidator.delegatedAmount?.toFixed(2) || '0.00'} AVO<br />
                  Pending Rewards: {selectedValidator.reward?.toFixed(6) || '0.00'} AVO
                </Alert>
                <TextField
                  fullWidth
                  label="Amount to Undelegate (AVO)"
                  value={actionAmount}
                  onChange={(e) => setActionAmount(e.target.value)}
                  variant="outlined"
                  placeholder={`Max: ${selectedValidator.delegatedAmount?.toFixed(2) || '0.00'} AVO`}
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      borderRadius: 0,
                      '& fieldset': { borderColor: '#FFFFFF' },
                      '&:hover fieldset': { borderColor: '#FFFFFF' },
                      '&.Mui-focused fieldset': { borderColor: '#FFFFFF' }
                    },
                    '& .MuiInputLabel-root': { 
                      color: '#FFFFFF',
                      '&.Mui-focused': { color: '#FFFFFF' }
                    }
                  }}
                />
                <Alert 
                  severity="warning" 
                  sx={{ 
                    mt: 2, 
                    backgroundColor: '#000000', 
                    border: '1px solid #FFFFFF',
                    borderRadius: 0,
                    color: '#FFFFFF',
                    '& .MuiAlert-icon': {
                      color: '#FFFFFF'
                    }
                  }}
                >
                  Rewards are claimed automatically when undelegating. Tokens are available immediately.
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid #FFFFFF' }}>
            <Button 
              onClick={() => setUndelegateDialog(false)}
              sx={{ color: '#FFFFFF', borderRadius: 0 }}
            >
              CANCEL
            </Button>
            <Button 
              onClick={handleUndelegateAll}
              variant="outlined"
              sx={{ 
                borderColor: '#FFFFFF', 
                color: '#FFFFFF',
                borderRadius: 0
              }}
            >
              UNDELEGATE ALL
            </Button>
            <Button 
              onClick={handleUndelegate}
              variant="outlined"
              sx={{ 
                borderColor: '#FFFFFF', 
                color: '#FFFFFF',
                borderRadius: 0
              }}
            >
              UNDELEGATE
            </Button>
          </DialogActions>
        </Dialog>

        {/* Redelegate Dialog */}
        <Dialog 
          open={redelegateDialog} 
          onClose={() => setRedelegateDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: '#000000',
              border: '1px solid #FFFFFF',
              borderRadius: 0
            }
          }}
        >
          <DialogTitle sx={{ borderBottom: '1px solid #FFFFFF', color: '#FFFFFF' }}>
            REDELEGATE TO ANOTHER VALIDATOR
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {selectedValidator && (
              <Box>
                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 2, 
                    backgroundColor: '#000000', 
                    border: '1px solid #FFFFFF',
                    borderRadius: 0,
                    color: '#FFFFFF',
                    '& .MuiAlert-icon': {
                      color: '#FFFFFF'
                    }
                  }}
                >
                  From: <strong>{selectedValidator.validatorName}</strong><br />
                  Available: {selectedValidator.delegatedAmount?.toFixed(2)} AVO
                </Alert>
                
                <Typography variant="body2" sx={{ mb: 1, color: '#FFFFFF' }}>
                  SELECT TARGET VALIDATOR
                </Typography>
                <Box sx={{ mb: 2, maxHeight: '200px', overflowY: 'auto', border: '1px solid #FFFFFF', p: 1 }}>
                  {validators
                    .filter(v => (v.id || v.validatorId) !== selectedValidator.validatorId)
                    .map((validator, index) => (
                      <Box
                        key={index}
                        onClick={() => setTargetValidator(validator)}
                        sx={{
                          p: 1,
                          mb: 1,
                          cursor: 'pointer',
                          backgroundColor: targetValidator === validator ? '#FFFFFF' : '#000000',
                          color: targetValidator === validator ? '#000000' : '#FFFFFF',
                          border: '1px solid #FFFFFF',
                          '&:hover': {
                            backgroundColor: targetValidator === validator ? '#FFFFFF' : '#222222'
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {validator.moniker || validator.validatorName}
                        </Typography>
                        <Typography variant="caption">
                          APR: {validator.apr}% | Commission: {validator.commission}%
                        </Typography>
                      </Box>
                    ))}
                </Box>

                <TextField
                  fullWidth
                  label="Amount to Redelegate (AVO)"
                  value={actionAmount}
                  onChange={(e) => setActionAmount(e.target.value)}
                  placeholder={`Max: ${selectedValidator.delegatedAmount?.toFixed(2) || '0.00'} AVO`}
                  variant="outlined"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      borderRadius: 0,
                      '& fieldset': { borderColor: '#FFFFFF' },
                      '&:hover fieldset': { borderColor: '#FFFFFF' },
                      '&.Mui-focused fieldset': { borderColor: '#FFFFFF' }
                    },
                    '& .MuiInputLabel-root': { 
                      color: '#FFFFFF',
                      '&.Mui-focused': { color: '#FFFFFF' }
                    }
                  }}
                />
                <Alert 
                  severity="info" 
                  sx={{ 
                    mt: 2, 
                    backgroundColor: '#000000', 
                    border: '1px solid #FFFFFF',
                    borderRadius: 0,
                    color: '#FFFFFF',
                    '& .MuiAlert-icon': {
                      color: '#FFFFFF'
                    }
                  }}
                >
                  Redelegation moves your stake instantly without unbonding period. Only one gas fee charged.
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid #FFFFFF' }}>
            <Button 
              onClick={() => setRedelegateDialog(false)}
              sx={{ color: '#FFFFFF', borderRadius: 0 }}
            >
              CANCEL
            </Button>
            <Button 
              onClick={handleRedelegate}
              variant="outlined"
              sx={{ 
                borderColor: '#FFFFFF', 
                color: '#FFFFFF',
                borderRadius: 0
              }}
            >
              REDELEGATE
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Component */}
        <Notification
          open={notification.open}
          message={notification.message}
          severity={notification.severity}
          details={notification.details}
          onClose={() => setNotification({ ...notification, open: false })}
        />
      </Box>
    </ThemeProvider>
  );
};

export default Delegators;